import {
  AnyCustomFieldDefinition,
  CustomFieldDefinition,
  FieldsetCustomFieldDefinition,
} from '@payaca/custom-fields';
import { useMemo } from 'react';

import { useProjectCustomFields } from '@/hooks/useProjectCustomFields';

/**
 * Flat representation of one node in the project Custom Field tree that's
 * eligible for selection in the customFieldCondition picker. Includes
 * non-fieldset Custom Fields (leaves) AND fieldsets at every depth — the
 * picker doesn't filter by type. The downstream operator dropdown then
 * consults `OPERATORS_BY_FIELD_TYPE[node.definition.type]` to surface only
 * the operators applicable to the picked node.
 */
export type CustomFieldPickerNode = {
  /**
   * Dotted identifier chain including the root fieldset, e.g.
   * `installationDetails.panelType` or `installationDetails`.
   */
  fieldPath: string;
  /** Resolved definition at this path. */
  definition: AnyCustomFieldDefinition;
  /**
   * Ancestor segment labels (root → parent), display-formatted from each
   * fieldset's `label`. Used to render the breadcrumb subordinate to
   * `definition.label`. Empty for top-level (root) nodes.
   */
  breadcrumb: string[];
};

const joinPath = (segments: readonly string[]): string => segments.join('.');

/**
 * Walks every project-role Custom Field, expanding each fieldset's
 * descendants via `getChildrenWithPaths()`. Includes the fieldsets
 * themselves at every depth (so fieldset operators
 * `allChildrenAreSet` etc. can target them) and non-fieldset Custom
 * Fields at the top level.
 */
export const buildCustomFieldPickerNodes = (
  projectCustomFields: ReturnType<
    typeof useProjectCustomFields
  >['projectCustomFields']
): CustomFieldPickerNode[] => {
  const out: CustomFieldPickerNode[] = [];

  for (const field of projectCustomFields) {
    if (field.archivedAt) continue;
    if (!field.schema) continue;

    let rootDefinition: AnyCustomFieldDefinition;
    try {
      // The GraphQL `CustomField.schema` is the package's JSON Schema
      // representation; `fromSchema` rehydrates the typed definition.
      rootDefinition = CustomFieldDefinition.fromSchema(
        field.schema as Parameters<typeof CustomFieldDefinition.fromSchema>[0]
      );
    } catch {
      continue;
    }

    if (rootDefinition instanceof FieldsetCustomFieldDefinition) {
      // The root fieldset is itself a valid pickable node — fieldset
      // operators (`allChildrenAreSet` etc.) target fieldset paths.
      out.push({
        fieldPath: rootDefinition.identifier,
        definition: rootDefinition,
        breadcrumb: [],
      });

      const childrenWithPaths = rootDefinition.getChildrenWithPaths();

      // Build a label lookup so breadcrumbs render display labels rather
      // than identifiers.
      const labelByPathKey = new Map<string, string>();
      labelByPathKey.set(
        joinPath([rootDefinition.identifier]),
        rootDefinition.label
      );
      for (const { definition, path } of childrenWithPaths) {
        labelByPathKey.set(joinPath(path), definition.label);
      }

      for (const { definition, path } of childrenWithPaths) {
        // Breadcrumb = labels of ancestors only; the node's own label is
        // the primary display text rendered alongside.
        const ancestorPaths = path.slice(0, -1);
        const breadcrumb = ancestorPaths.map((_segment, i) => {
          const subPath = path.slice(0, i + 1);
          return labelByPathKey.get(joinPath(subPath)) ?? subPath.at(-1) ?? '';
        });

        out.push({
          fieldPath: joinPath(path),
          definition,
          breadcrumb,
        });
      }
    } else {
      // Top-level non-fieldset Custom Fields are unusual but possible.
      out.push({
        fieldPath: rootDefinition.identifier,
        definition: rootDefinition,
        breadcrumb: [],
      });
    }
  }

  return out;
};

/**
 * Returns the flat list of pickable nodes for the caller's project
 * Custom Fields, plus a `nodesByPath` Map for O(1) lookup by fieldPath.
 * Memoised on the underlying `projectCustomFields` reference so consumers
 * can rely on referential stability.
 */
export const useCustomFieldPickerNodes = () => {
  const { projectCustomFields, isLoading, ...rest } = useProjectCustomFields();
  const nodes = useMemo(
    () => buildCustomFieldPickerNodes(projectCustomFields),
    [projectCustomFields]
  );
  const nodesByPath = useMemo(() => {
    const map = new Map<string, CustomFieldPickerNode>();
    for (const node of nodes) map.set(node.fieldPath, node);
    return map;
  }, [nodes]);
  return { nodes, nodesByPath, isLoading, ...rest };
};
